import { Injectable } from '@angular/core';
import { resource } from "@util/resource";
import * as _ from "lodash";
import { Observable } from "rxjs";
import { DWS } from "@interfaces/dws";
import { OutlookAuthToken } from "@interfaces/dws/Outlook-auth-token";
import { MsalService } from "@azure/msal-angular";
import { AuthenticationResult, AuthError } from "@azure/msal-browser";

/** Result of Wine Suite Microsoft login popup (openid/profile/email). */
export type WineSuiteMicrosoftLoginResult =
  | { ok: true; idToken: string }
  | { ok: false; reason: "cancelled" | "failed"; detail?: string };

@Injectable({
  providedIn: 'root'
})
export class OutlookAuthService {

  constructor(
      private msalService: MsalService
  ) {
  }

  authorize(): Promise<any> {
    return this.msalService.loginPopup({
      scopes: ['User.Read', 'MailboxSettings.Read', 'Calendars.ReadWrite', 'openid', 'offline_access'],
    }).toPromise();
  }

  /**
   * Minimal OIDC scopes for Wine Suite login (same Azure app as Outlook calendar).
   * Returns the raw {@code id_token} for POST /account/authenticate-microsoft.
   */
  loginForWineSuiteAccount(): Promise<WineSuiteMicrosoftLoginResult> {
    return this.msalService
      .loginPopup({
        scopes: ["openid", "profile", "email"],
      })
      .toPromise()
      .then((result: AuthenticationResult): WineSuiteMicrosoftLoginResult => {
        if (result?.idToken) {
          return { ok: true, idToken: result.idToken };
        }
        return {
          ok: false,
          reason: "failed",
          detail: "no_id_token",
        };
      })
      .catch((e: unknown): WineSuiteMicrosoftLoginResult => {
        const err = e as AuthError;
        const code = err?.errorCode ?? "";
        const msg = String(err?.errorMessage ?? err?.message ?? e ?? "");
        if (
          code === "user_cancelled" ||
          /user_cancelled|User cancelled/i.test(msg)
        ) {
          return { ok: false, reason: "cancelled" };
        }
        if (code === "popup_window_error" || /popup.*blocked|blocked.*popup/i.test(msg)) {
          return {
            ok: false,
            reason: "failed",
            detail: "popup_blocked",
          };
        }
        const detail = [code, msg].filter(Boolean).join(": ").slice(0, 300);
        if (detail) {
          console.warn("[MSAL] Wine Suite login failed:", detail);
        }
        return { ok: false, reason: "failed", detail };
      });
  }

  list(filters?: OutlookAuthTokenFilters, page: number = 0, count: number = 999999): Observable<DWS.Page<OutlookAuthToken>> {
    return resource('crm://outlook-auth-tokens')
        .params(
            { page, count, ...filters }
        )
        .get<DWS.Page<OutlookAuthToken>>();
  }

  delete(id: string): Observable<void> {
    return resource('crm://outlook-auth-tokens/id')
        .params( { id })
        .delete()
  }

  link(credentials: any, wineryId: string) {
    return resource('crm://outlook-auth-tokens')
        .post(_.merge(credentials, { wineryId }));
  }
}

export interface OutlookAuthTokenFilters {
  wineryId: string;
}